import { PlusIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';
import { Input, InputWithIcon } from '../common/Input';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '../common/Popover';
import { useStore } from '@/hooks/useStore';
import { flowResult, toJS } from 'mobx';
import debounce from 'lodash.debounce';
import { toastify } from '@/utils/toastify';
import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../common/Form';
import { Label } from '../common/Label';
import { Textarea } from '../common/Textarea';
import { Button } from '../common/Button';

interface SearchInputProps {
    className?: string,
    shopCode?: string,
    valueSearch: string,
    initialDisplayValue?: string | any,
    setValueSearch: (value: string) => void
}

export const getServiceShopNameByServiceName = (serviceName: string, shopServices) => {
    if (shopServices && shopServices.length > 0) {
        const service = shopServices.find(shop => shop?.serviceName === serviceName);
        if (service) {
            return service?.serviceShopName;
        }
    }
    return '';
};

export default observer(function SearchShopServiceInput({ className = '', shopCode, valueSearch, setValueSearch, initialDisplayValue = null }: SearchInputProps) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLElement | any>(null);
    const [openPopoverSearch, setOpenPopoverSearch] = useState<boolean>(false);

    const [value, setValue] = useState<string>('');
    const [valueSearchCurrent, setValueSearchCurrent] = useState<string>('');

    const [shopServices, setShopServices] = useState<any[]>([]);
    const [isSelectingService, setIsSelectingService] = useState<boolean>(false); // Cờ để kiểm soát onBlur
    const [idShopCurrent, setIdShopCurrent] = useState<number | null>(null);

    const { shopStore: { getAllShopService, deleteShopService, shopServiceIdUpdate },
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    const debouncedSearch = useCallback(
        debounce(async (searchValue: string) => {
            const res = await flowResult(getAllShopService(shopCode, { name: searchValue, page: 1, size: 50 })) as any[];
            setShopServices(res as any[]);
            setOpenPopoverSearch(true);
        }, 500),
        [shopCode, getAllShopService]
    );

    const loadSearchNow = async (searchValue: string) => {
        const res = await flowResult(getAllShopService(shopCode, { name: searchValue, page: 1, size: 50 })) as any[];
        setShopServices(res as any[]);
        setOpenPopoverSearch(true);
    };

    useEffect(() => {
        setValue(getServiceShopNameByServiceName(valueSearch, shopServices));
        setValueSearchCurrent(getServiceShopNameByServiceName(valueSearch, shopServices));
    }, [valueSearch]);

    // Set thông tin bàn đầu cho ô search
    useEffect(() => {
        setInitValue(false);
    }, []);

    // Khi chỉnh sửa tên sẽ cập nhật lại giá trị nếu ô input đang có giá trị là bản ghi bị thay đổi
    useEffect(() => {
        if (valueSearch === shopServiceIdUpdate) {
            setInitValue(true);
        }
    }, [shopServiceIdUpdate]);

    const setInitValue = async (isUpdate = false) => {
        if (isUpdate) {
            const res = await flowResult(getAllShopService(shopCode, { name: '', page: 1, size: 50 })) as any[];
            const iValue = getServiceShopNameByServiceName(valueSearch, res as any[]);
            setValue(iValue);
            setValueSearchCurrent(iValue);
        } else {
            setValue(initialDisplayValue || '');
            setValueSearchCurrent(initialDisplayValue || '');
        }
    };

    const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setValue(value);
        debouncedSearch(value);
    };

    const handleClick = () => {
        if (valueSearchCurrent === value) {
            setValue('');
            // debouncedSearch('');
            loadSearchNow('');
        }
    };

    const handleClickShopService = (event: any, service: any) => {
        if (valueSearchCurrent === service.serviceShopName) {
            setValue(valueSearchCurrent);
        }

        setValueSearch(service?.serviceName);
        setIdShopCurrent(service?.id);
        setOpenPopoverSearch(false);
        setIsSelectingService(false); // Reset cờ
    };

    const handleMouseDownShopService = (service: any) => {
        setIsSelectingService(true); // Đặt cờ trước khi onBlur xảy ra
    };

    const handleBlur = () => {
        if (!isSelectingService) {
            setValue(valueSearchCurrent); // Chỉ đặt lại giá trị cũ nếu không chọn dịch vụ
        }
    };

    const handleClickBtnShopService = (type: string = FormShopServiceType.create, shopServiceId, serviceShopName, description) => {
        setOpenPopoverSearch(false);
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: <FormShopService shopCode={shopCode} type={type} shopServiceId={shopServiceId} serviceNameEdit={serviceShopName} description={description} />,
            footerClassName: 'hidden',
            title: type === FormShopServiceType.create ? t('words_title.title_create_shop_service') : t('words_title.title_put_shop_service'),
            showHeader: true,
            contentClassName: 'w-full mt-[-30px]'
        }));
    };

    const handleClickDeleteShopService = (shopId) => {
        setOpenPopoverSearch(false);
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_delete_shop_service'),
            onSave: async () => {
                const res = await flowResult(deleteShopService(shopId));
                if (res && res.ok) {
                    toastify('success', t('messages.update_success'));
                    if (idShopCurrent === shopId) {
                        setIdShopCurrent(null);
                        setValueSearch('');
                    }
                }
            }
        }));
    };

    return (
        <div>
            <Popover open={openPopoverSearch}>
                <PopoverTrigger className='w-full'>
                    <InputWithIcon
                        ref={inputRef}
                        type='text'
                        placeholder={(valueSearchCurrent && valueSearchCurrent !== '') ? valueSearchCurrent : t('placeholder.search_input_shop_service')}
                        value={value}
                        onChange={onChange}
                        onClick={handleClick}
                        onBlur={handleBlur}
                        className={className}
                        maxLength={70}
                    />
                </PopoverTrigger>
                <PopoverContent
                    className={`p-1 max-h-[192px] overflow-auto !w-[${inputRef.current?.offsetWidth || 294}px]`}
                    align='start'
                    autoFocus={false}
                    widthFull={true}
                    onOpenAutoFocus={e => e.preventDefault()}
                    onPointerDownOutside={() => setOpenPopoverSearch(false)}
                >
                    <div className='divide-y divide-gray-200'>
                        <div
                            className='py-2 px-1 text-sm items-center gap-2 hover:cursor-pointer hover:bg-slate-200 flex justify-center'
                            onClick={e => handleClickBtnShopService(FormShopServiceType.create, null, '', '')}
                        >
                            <span className='text-[#9A6051] font-semibold'>
                                <PlusIcon className='inline-block align-sub mr-1 text-[#9A6051]' />
                                {t('button.add_service')}
                            </span>
                        </div>
                        {
                            (shopServices && shopServices.length > 0) ?
                                shopServices.map((shop, index) => (
                                    <div
                                        key={index}
                                        className='py-2 px-1 text-sm flex items-center gap-2 hover:cursor-pointer hover:bg-slate-200 justify-between'
                                    >
                                        <span
                                            className='truncate-text grow'
                                            title={shop?.serviceShopName}
                                            onMouseDown={() => handleMouseDownShopService(shop)} // Xử lý trước onBlur
                                            onClick={e => handleClickShopService(e, shop)}
                                        >
                                            {shop?.serviceShopName}
                                        </span>
                                        <div className='flex '>
                                            <SquarePenIcon className='mr-2' onClick={() => handleClickBtnShopService(FormShopServiceType.edit, shop.id, shop?.serviceShopName, shop?.description)} />
                                            <Trash2Icon onClick={() => handleClickDeleteShopService(shop?.id)} />
                                        </div>
                                    </div>
                                )) :
                                (
                                    <div
                                        className='py-2 px-1 text-sm flex items-center h-[100px]'
                                    >
                                        <div className='text-center w-full'>{t('sentences.no_shop_service_found')}</div>
                                    </div>
                                )
                        }
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
});

const FormShopServiceType = {
    edit: 'edit',
    create: 'create'
};

interface FormShopServiceProps {
    shopCode?: string,
    type?: string,
    serviceNameEdit?: string,
    shopServiceId?: number,
    description?: string
}

const validateSchema = yup.object().shape({
    serviceName: yup.string().required('form_error_validate.required'),
    description: yup.string()
});

type FormData = yup.InferType<typeof validateSchema>;

const FormShopService = observer(({ shopCode, type, shopServiceId, serviceNameEdit = '', description = '' }: FormShopServiceProps) => {
    const { t } = useTranslation();
    const { shopStore: { createShopService, putShopService },
        modalStore: { hideAllModals }
    } = useStore();

    const initialValues = {
        serviceName: type === FormShopServiceType.edit ? serviceNameEdit : '',
        description: type === FormShopServiceType.edit ? description : ''
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const onSubmit = async (data) => {
        const { serviceName, description } = data;
        let payload = { serviceShopName: serviceName, description };
        if (type === FormShopServiceType.create) {
            const res = await flowResult(createShopService(shopCode, payload));
            if (res && res.ok) {
                toastify('success', t('messages.update_success'));
                hideAllModals();
            }
        } else if (type === FormShopServiceType.edit && shopServiceId) {
            let payloadEdit = { ...payload, shopServiceId };
            const res = await flowResult(putShopService(shopCode, payloadEdit));
            if (res && res.ok) {
                toastify('success', t('messages.update_success'));
                hideAllModals();
            }
        }
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <FormField
                        control={form.control}
                        name='serviceName'
                        render={({ field }) => (
                            <FormItem className='flex flex-col mt-4 w-full'>
                                <Label className='label-1 text-left' required>{t('words_title.service_name_input')}</Label>
                                <FormControl className='mt-1.5'>
                                    <Input
                                        {...field}
                                        placeholder={t('placeholder.service_name_input')}
                                    />
                                </FormControl>
                                <FormMessage className='text-left' />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='description'
                        render={({ field }) => (
                            <FormItem className='flex flex-col mt-4 w-full'>
                                <Label className='label-1 text-left'>{t('words_title.description_input')}</Label>
                                <FormControl className='mt-1.5'>
                                    <Textarea maxLength={1000} className='text-input-1 min-h-[200px]' placeholder={t('placeholder.st_about_service')} {...field} />
                                </FormControl>
                                <FormMessage className='text-left' />
                            </FormItem>
                        )}
                    />
                    <div className='flex justify-between mb-3 mt-5'>
                        <Button variant='outline' className='flex-1 mr-5' onClick={() => hideAllModals()}>{t('words_title.cancel')}</Button>
                        <Button variant='default' className='flex-1'>{t('button.save')}</Button>
                    </div>
                </form>
            </Form>
        </>
    );
});
